/**
 * stuburt-eager-ahead.js
 *
 * Native loading="lazy" only begins downloading an image when it is almost on-screen,
 * using a browser-fixed margin you cannot configure. On a slow connection that means
 * images visibly trickle in as you scroll — and any scroll-reveal (<stuburt-reveal>)
 * makes the blank gap obvious because content is hidden until shown.
 *
 * This preloads lazy <img>/<iframe> WELL ahead of the fold by promoting them to
 * loading="eager" once they enter a generous look-ahead margin, at LOW fetch priority
 * so the ahead-of-view images never compete with the hero / LCP / critical resources.
 * Result: images are already downloaded by the time they scroll into view, even on a
 * poor connection — while the page still doesn't download everything at once.
 *
 * Reveal (opacity/transform) is purely visual and unaffected; this only changes WHEN
 * the bytes are fetched. No <img> markup, srcset, or sizes is touched.
 */

// How far below the fold to start loading. ~2 viewport-heights of lead time — raise this
// (e.g. '300%') for an even bigger buffer on very slow connections; lower it to save data.
const PRELOAD_MARGIN = '0px 0px 200% 0px';
const LAZY_SELECTOR = 'img[loading="lazy"], iframe[loading="lazy"]';

if (!window.__stuburtEagerAhead) {
  window.__stuburtEagerAhead = true;

  /** Kick off the download now, at a priority that won't starve the LCP. */
  const promote = (el) => {
    if (!el || el.dataset.eagerAhead) return;
    el.dataset.eagerAhead = '1';
    try {
      el.fetchPriority = 'low';
    } catch (e) {
      /* older browsers ignore fetchPriority */
    }
    el.loading = 'eager'; // changing lazy -> eager starts the fetch immediately
  };

  const observer = new IntersectionObserver(
    (entries, obs) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          promote(entry.target);
          obs.unobserve(entry.target);
        }
      }
    },
    { rootMargin: PRELOAD_MARGIN }
  );

  const observeWithin = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    for (const el of scope.querySelectorAll(LAZY_SELECTOR)) {
      if (!el.dataset.eagerAhead) observer.observe(el);
    }
  };

  const start = () => {
    observeWithin(document);

    // Catch lazy media injected later (carousels, recently-viewed, section re-renders).
    const mutations = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(LAZY_SELECTOR) && !node.dataset.eagerAhead) {
            observer.observe(node);
          }
          if (node.querySelectorAll) observeWithin(node);
        }
      }
    });
    mutations.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}
